import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as z from 'zod';
import axios from 'axios';
import {
  MapPin, Navigation, Building2, User, Phone, Mail,
  Map, Globe, FileText, IdCard, CheckCircle,
  AlertCircle, Loader2, Home, Briefcase, Landmark
} from 'lucide-react';

// Validation Schema
const traderSchema = z.object({
  tin: z.string().min(5, "TIN must be at least 5 characters").max(20),
  phone: z.string().regex(/^(\+?[0-9]{10,15})$/, "Invalid phone format"),
  email: z.string().email("Invalid email").optional().or(z.literal('')),
  city: z.string().min(2),
  business_name: z.string().min(3),
  subcity: z.string().min(2),
  name: z.string().min(2),
  woreda: z.string().min(1),
  region: z.string().min(2),
  address: z.string().min(5),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  license_photo: z.any().optional(),
  national_id_photo: z.any().optional(),
  status: z.enum(['pending', 'approved', 'rejected']).default('pending'),
});

const ethiopianRegions = [
  { value: 'addis_ababa', label: 'Addis Ababa' },
  { value: 'afar', label: 'Afar Region' },
  { value: 'amhara', label: 'Amhara Region' },
  { value: 'benishangul_gumuz', label: 'Benishangul-Gumuz' },
  { value: 'dire_dawa', label: 'Dire Dawa' },
  { value: 'gambela', label: 'Gambela' },
  { value: 'harari', label: 'Harari' },
  { value: 'oromia', label: 'Oromia' },
  { value: 'sidama', label: 'Sidama' },
  { value: 'somalia', label: 'Somali' },
  { value: 'south_west_ethiopia', label: 'South West Ethiopia' },
  { value: 'southern_nations', label: 'SNNPR' },
  { value: 'tigray', label: 'Tigray' }
];

export default function AddTrader({ onClose, onSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [locationDetected, setLocationDetected] = useState(false);
  const navigate = useNavigate();

  // Axios Instance - NO global Content-Type
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'https://api.cdaethi.org/api',
    headers: { Accept: 'application/json' },
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(traderSchema),
    defaultValues: { status: 'pending', region: '', email: '' },
  });

  const latitude = watch('latitude');
  const longitude = watch('longitude');

  // Auth Check
  useEffect(() => {
    if (!localStorage.getItem('token')) navigate('/');
  }, [navigate]);

  // Auto Location
  useEffect(() => {
    const getLocation = () => {
      if (!navigator.geolocation) return;
      setIsLocating(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setValue('latitude', pos.coords.latitude);
          setValue('longitude', pos.coords.longitude);
          setLocationDetected(true);
          setIsLocating(false);
        },
        () => setIsLocating(false),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    };
    const timer = setTimeout(getLocation, 800);
    return () => clearTimeout(timer);
  }, [setValue]);

  const handleGetLocation = () => {
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setValue('latitude', pos.coords.latitude);
        setValue('longitude', pos.coords.longitude);
        setLocationDetected(true);
        setIsLocating(false);
        Swal.fire({ icon: 'success', title: 'Location Updated!', timer: 1500, toast: true });
      },
      () => {
        setIsLocating(false);
        Swal.fire({ icon: 'error', title: 'Location Failed' });
      }
    );
  };

  // ====================== MULTIPART FORM DATA SUBMISSION ======================
  const onSubmit = async (data) => {
    if (!data.latitude || !data.longitude) {
      Swal.fire({
        icon: 'warning',
        title: 'Location Required',
        text: 'Please detect your location first.',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();

      Object.keys(data).forEach((key) => {
        if (key === 'license_photo' || key === 'national_id_photo') {
          if (data[key]?.[0]) {
            formData.append(key, data[key][0]);        // ← This sends the actual image file
          }
        } else if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
          formData.append(key, data[key]);
        }
      });

      // This is the key line - sending multipart data
      await api.post('/addtrader', formData);

      Swal.fire({
        icon: 'success',
        title: 'Trader Registered Successfully!',
        timer: 3000,
        showConfirmButton: false,
      });

      reset();
      setLocationDetected(false);
      onSuccess?.();
      onClose?.();

    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: 'error',
        title: 'Registration Failed',
        text: error.response?.data?.message || 'Something went wrong. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reusable Components
  const InputField = ({ icon: Icon, label, name, placeholder, type = "text", required = true, ...props }) => (
    <div className="space-y-1.5">
      <label className="block text-sm font-semibold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type={type}
          {...register(name)}
          className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 transition-all ${
            errors[name] ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-indigo-500'
          }`}
          placeholder={placeholder}
          {...props}
        />
      </div>
      {errors[name] && <p className="text-red-500 text-xs">{errors[name].message}</p>}
    </div>
  );

  const FileField = ({ label, name }) => (
    <div className="space-y-1.5">
      <label className="block text-sm font-semibold text-gray-700">{label}</label>
      <input
        type="file"
        {...register(name)}
        accept="image/jpeg,image/png,image/jpg"
        className="w-full text-sm text-gray-500 file:mr-4 file:py-3 file:px-6 file:rounded-xl 
                   file:border-0 file:text-sm file:font-medium file:bg-indigo-50 
                   file:text-indigo-700 hover:file:bg-indigo-100 border border-gray-300 rounded-xl cursor-pointer"
      />
      {errors[name] && <p className="text-red-500 text-xs mt-1">{errors[name].message}</p>}
    </div>
  );

  const SelectField = ({ label, name, options }) => (
    <div className="space-y-1.5">
      <label className="block text-sm font-semibold text-gray-700">{label} <span className="text-red-500">*</span></label>
      <select
        {...register(name)}
        className={`w-full py-3 px-4 border rounded-xl focus:ring-2 focus:ring-indigo-500 ${
          errors[name] ? 'border-red-400' : 'border-gray-300'
        }`}
      >
        <option value="">Select Region</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {errors[name] && <p className="text-red-500 text-xs">{errors[name].message}</p>}
    </div>
  );

  const TextAreaField = ({ label, name, placeholder }) => (
    <div className="space-y-1.5">
      <label className="block text-sm font-semibold text-gray-700">{label} <span className="text-red-500">*</span></label>
      <textarea
        {...register(name)}
        rows={3}
        placeholder={placeholder}
        className={`w-full p-4 border rounded-xl focus:ring-2 focus:ring-indigo-500 ${
          errors[name] ? 'border-red-400' : 'border-gray-300'
        }`}
      />
      {errors[name] && <p className="text-red-500 text-xs">{errors[name].message}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-10 text-white">
            <h1 className="text-4xl font-bold">Register New Trader</h1>
            <p className="mt-2 text-indigo-100">Complete all required fields</p>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-10">
            {/* Business Information */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Building2 className="h-6 w-6 text-indigo-600" />
                <h2 className="text-2xl font-semibold">Business Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField icon={Landmark} label="TIN Number" name="tin" placeholder="ET0123456789" />
                <InputField icon={Briefcase} label="Business Name" name="business_name" placeholder="Business name" />
              </div>
            </div>

            {/* Personal Information */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <User className="h-6 w-6 text-indigo-600" />
                <h2 className="text-2xl font-semibold">Personal Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField icon={User} label="Full Name" name="name" placeholder="Full name" />
                <InputField icon={Phone} label="Phone Number" name="phone" placeholder="0912345678" />
                <InputField icon={Mail} label="Email" name="email" type="email" required={false} />
              </div>
            </div>

            {/* Address Information */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Map className="h-6 w-6 text-indigo-600" />
                <h2 className="text-2xl font-semibold">Address Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SelectField label="Region" name="region" options={ethiopianRegions} />
                <InputField icon={Globe} label="City" name="city" placeholder="Addis Ababa" />
                <InputField icon={MapPin} label="Subcity" name="subcity" placeholder="Bole" />
                <InputField icon={MapPin} label="Woreda" name="woreda" placeholder="01" />

                <div className="md:col-span-2 grid grid-cols-2 gap-6">
                  <InputField icon={MapPin} label="Latitude" name="latitude" type="number" readOnly disabled />
                  <InputField icon={MapPin} label="Longitude" name="longitude" type="number" readOnly disabled />
                </div>
              </div>

              <div className="mt-6">
                <TextAreaField label="Full Address" name="address" placeholder="Detailed address..." />
              </div>
            </div>

            {/* Documents - This is where images are sent */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <FileText className="h-6 w-6 text-indigo-600" />
                <h2 className="text-2xl font-semibold">Supporting Documents</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FileField label="Business License Photo" name="license_photo" />
                <FileField label="National ID Photo" name="national_id_photo" />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white py-4 rounded-2xl font-bold text-lg transition-all disabled:opacity-70 flex items-center justify-center gap-3"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-6 w-6 animate-spin" />
                  Registering...
                </>
              ) : (
                <>
                  <CheckCircle className="h-6 w-6" />
                  Register Trader
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}